import { hasQueryOverrides } from 'components/V5Gadget/seriesDataKey';

/**
 * A secondary row dimension is supported on a matrix whose rows are a
 * categorical dimensionA, with un-reversed axes, and either:
 * - columns of a date trend (dimensionB) and a single metric: the rows
 *   request nests groupBy (A) -> subGroupBy (secondary A) -> trend (B)
 * - a column per metric (no dimensionB, no trend), where no column asks its
 *   own question: groupBy (A) -> subGroupBy (secondary A). Studio's Tables
 *   draw these; the classic editor only offers date columns, so it doesn't
 *   pass isMetricColumnsAllowed.
 */
const isSecondaryDimensionAEligible = ({
  gadgetType,
  dimensionA,
  dimensionB,
  seriesCount,
  reverseAxes,
  isMetricColumnsAllowed,
  isTrended,
  hasSeriesQueryOverrides,
}: {
  gadgetType: GadgetType;
  dimensionA?: Dimension;
  dimensionB?: Dimension;
  seriesCount: number;
  reverseAxes?: boolean;
  isMetricColumnsAllowed?: boolean;
  isTrended?: boolean;
  hasSeriesQueryOverrides?: boolean;
}): boolean => {
  if (gadgetType !== 'matrix' || reverseAxes) {
    return false;
  }
  if (
    !dimensionA ||
    dimensionA.rangeInput ||
    (dimensionA.fieldType !== 'text' && dimensionA.fieldType !== 'boolean')
  ) {
    return false;
  }

  if (!dimensionB) {
    return !!isMetricColumnsAllowed && !isTrended && !hasSeriesQueryOverrides;
  }

  return (
    seriesCount === 1 &&
    dimensionB.field === 'date' &&
    dimensionB.fieldType === 'date'
  );
};

// Whether a chart's secondaryDimensionA splits its rows: sent as subGroupBy,
// flattened to a row per (A, secondary A) pair, and drawn as a column beside
// A. Otherwise it's left over (e.g. from a layout that had it) and ignored.
export const isSecondaryDimensionAInEffect = (
  chartDef: V5ChartDefinition,
): boolean => {
  const { secondaryDimensionA, dimensionA } = chartDef;
  if (
    !secondaryDimensionA ||
    !secondaryDimensionA.fieldType ||
    !dimensionA ||
    secondaryDimensionA.field === dimensionA.field ||
    secondaryDimensionA.field === 'id'
  ) {
    return false;
  }

  return isSecondaryDimensionAEligible({
    gadgetType: chartDef.gadgetType,
    dimensionA,
    dimensionB: chartDef.dimensionB,
    seriesCount: chartDef.series.length,
    reverseAxes: chartDef.reverseAxes,
    isMetricColumnsAllowed: true,
    isTrended: !!(
      chartDef.trendByCalendarInterval ||
      chartDef.trendByFixedIntervalDays ||
      chartDef.trendByFiscalInterval ||
      chartDef.trendByHourInterval
    ),
    hasSeriesQueryOverrides: chartDef.series.some((s) =>
      hasQueryOverrides(s.queryOverrides),
    ),
  });
};

export default isSecondaryDimensionAEligible;
